import { getAuth } from "firebase/auth";
import { DATA_API_BASE_URL } from "../config/api";
import type { CountryCode, MapType } from "../config/countries";
import type { AreaShape } from "../geo/areaShapes";
import { getDemoAvailableMaps, getDemoGeosByIds, getDemoMapData, isDemoRouted } from "./demoApi";
import { catalogueServesMaps, getCatalogueAvailableMaps, getCatalogueGeosByIds, getCatalogueMapData } from "./catalogueMapsApi";

const BASE_URL = DATA_API_BASE_URL;

// The area types now live in the country registry, which says which country serves which.
export type { MapType };

export interface AvailableMap {
  table_name?: string;
  eng_label?: string;
  local_label?: string;
  country_code?: string;
  description?: string;
}

export interface AvailableMapsResponse {
  columns: string[];
  country_code?: string;
  results: AvailableMap[];
  /** True when these are the catalogue 1.0 maps (new areas are saved with the catalogue's country code). */
  catalogue?: boolean;
}

export interface BoundingBox {
  lng1: number;
  lat1: number;
  lng2: number;
  lat2: number;
}

export interface MapGeometry {
  geo_id: string;
  /** The area's name where the data has one. */
  name?: string | null;
  /** Grid cells only: the cell's side in metres. */
  size_m?: number;
  /** Catalogue model-grid cells only: the keys of the larger cells that hold this one. */
  parents?: string[];
  /** The shape of the cell: WKT text from the tile request, or GeoJSON from the area shapes loader. */
  geometry: string | AreaShape;
}

export interface ApiMapGeometry {
  geo_id: string;
  geometry: any;
}

export interface MapsApiResponse {
  type: MapType;
  bbox: {
    lng1: string;
    lat1: string;
    lng2: string;
    lat2: string;
  };
  results: MapGeometry[];
  /** True when the box held more areas than can be drawn; results is then empty and the picker asks to zoom in. */
  truncated?: boolean;
}

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const auth = getAuth();
  const user = auth.currentUser;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (user) {
    try {
      const idToken = await user.getIdToken();
      headers["Authorization"] = `Bearer ${idToken}`;
    } catch (error) {
      console.error("Failed to get Firebase ID token:", error);
      throw new Error("Authentication required - please log in");
    }
  } else {
    throw new Error("User not authenticated - please log in");
  }

  return headers;
};

export const mapsApi = {
  async getMapData(
    type: MapType,
    boundingBox: BoundingBox,
    countryCode: CountryCode,
    catalogue = false,
    /** The map's zoom: the catalogue's model grid draws the rung that suits it. */
    zoom?: number
  ): Promise<MapsApiResponse> {
    if (catalogue) return getCatalogueMapData(type, boundingBox, countryCode, zoom);
    if (isDemoRouted(countryCode)) return getDemoMapData(type, boundingBox, countryCode);
    const { lng1, lat1, lng2, lat2 } = boundingBox;
    const url = `${BASE_URL}/maps?geo_type=${type}&lng1=${lng1}&lat1=${lat1}&lng2=${lng2}&lat2=${lat2}&country_code=${countryCode}`;

    console.log("Making request to:", url);
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    console.log("Response status:", response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.log("Error response body:", errorText);
      throw new Error(`Failed to fetch map data: ${response.statusText}`);
    }

    return response.json();
  },

  async getGeosByIds(
    type: MapType, // Note: osm_links support coming soon
    geoIds: string[],
    countryCode: CountryCode,
    catalogue = false
  ): Promise<MapsApiResponse> {
    if (catalogue) return getCatalogueGeosByIds(type, geoIds, countryCode);
    if (isDemoRouted(countryCode)) return getDemoGeosByIds(type, geoIds, countryCode);
    const params = new URLSearchParams({
      geo_type: type,
      geo_ids: geoIds.join(","),
      country_code: countryCode,
    });
    const url = `${BASE_URL}/maps/by-ids?${params.toString()}`;

    console.log("Making request to:", url);
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    console.log("Response status:", response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.log("Error response body:", errorText);
      throw new Error(`Failed to fetch geos by IDs: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * The layers for new areas in a country: the catalogue 1.0 maps where the data API lists them, else the old maps.
   * `legacy` asks for the old maps anyway (editing an area made on them).
   */
  async getAvailableMaps(countryCode: CountryCode, legacy = false): Promise<AvailableMapsResponse> {
    if (!legacy && (await catalogueServesMaps(countryCode).catch(() => false))) {
      return { ...(await getCatalogueAvailableMaps(countryCode)), catalogue: true };
    }
    if (isDemoRouted(countryCode)) return getDemoAvailableMaps(countryCode);
    const url = `${BASE_URL}/maps/list?country_code=${countryCode}`;

    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorText = await response.text();
      console.log("Error response body:", errorText);
      throw new Error(`Failed to fetch available maps: ${response.statusText}`);
    }

    return response.json();
  },
};
